<template>
    <div
        ref="playtokWrapRef"
        class="playtok-wrap"
        @scroll="handleScroll"
        @wheel.capture="handleWrapWheel"
        @touchstart.capture.passive="handleWrapTouchStart"
        @touchmove.capture="handleWrapTouchMove"
        @touchend.capture.passive="resetTouchState"
        @touchcancel.capture.passive="resetTouchState"
        :style="{ backgroundColor: bgColor }"
    >
        <slot v-if="$slots.header" name="header" :opa="opa" :isScrolling="isScrolling"></slot>
        <div v-else class="header" :style="{ background: `rgba(42, 45, 61, ${opa})`, backgroundColor: headerBg }">
            <div
                v-if="showBack"
                :class="[
                    'header-back',
                    {
                        hide: !showBack,
                    },
                ]"
                @click="handleBack"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="56" height="54" viewBox="0 0 56 54" fill="none">
                    <rect width="56" height="54" rx="12" fill="#382B4A" />
                    <path
                        d="M31.8378 14.5026C32.6676 15.3324 32.6675 16.6779 31.8378 17.5078L22.1786 27.167L31.8378 36.8262C32.6676 37.6559 32.6674 39.0015 31.8378 39.8314C31.0079 40.6612 29.6625 40.6612 28.8326 39.8314L17.6839 28.6827C17.6796 28.6784 17.6745 28.6746 17.6701 28.6703C17.6184 28.6185 17.5704 28.5642 17.5251 28.5087C17.4343 28.3974 17.3559 28.2793 17.291 28.1558C17.2262 28.0325 17.1745 27.904 17.1356 27.7726C17.1078 27.6785 17.0888 27.5825 17.0742 27.486C17.0631 27.4122 17.0568 27.3381 17.0535 27.2637C17.0305 26.7561 17.1853 26.2418 17.5244 25.826L17.6708 25.6644L28.8326 14.5026C29.6624 13.6729 31.008 13.6729 31.8378 14.5026Z"
                        fill="#8E80A8"
                    />
                </svg>
            </div>
            <div class="header-title">{{ $t(title || route.meta.title) }}</div>
            <slot v-if="$slots.right" name="right"></slot>
            <div v-else class="header-right"></div>
        </div>
        <main class="wrap-main">
            <slot></slot>
        </main>
    </div>
</template>
<script setup lang="ts">
import { useHeaderHook } from '@/hooks/useHeaderHook'

const props = defineProps({
    title: {
        type: String,
        default: () => '',
    },
    showBack: {
        type: Boolean,
        default: () => true,
    },
    backFun: {
        type: Boolean,
        default: () => false,
    },
    headerBg: {
        type: String,
        default: () => '',
    },
    bgColor: {
        type: String,
        default: () => '',
    },
})
const emit = defineEmits(['back'])

const { opa, isScrolling, handleScroll } = useHeaderHook()
const playtokWrapRef = ref<HTMLElement | null>(null)
const touchState = {
    active: false,
    x: 0,
    y: 0,
    startScrollTop: 0,
    locked: false,
    axis: '' as '' | 'x' | 'y',
}

const router = useRouter()
const route = useRoute()

const shouldHijackTouch = (target: HTMLElement | null) => {
    return !!target?.closest('.swiper, .van-swipe, .van-tabs, .list-scroll, .game-item, .foru-list')
}

const handleWrapWheel = (event: WheelEvent) => {
    const el = playtokWrapRef.value
    const target = event.target as HTMLElement | null
    if (el && target?.closest('.swiper, .van-swipe, .van-tabs')) {
        const factor = event.deltaMode === 1 ? 40 : event.deltaMode === 2 ? el.clientHeight : 1
        event.preventDefault()
        el.scrollBy({
            top: event.deltaY * factor,
        })
    }
}

const handleWrapTouchStart = (event: TouchEvent) => {
    const touch = event.touches[0]
    const target = event.target as HTMLElement | null
    const el = playtokWrapRef.value
    if (!touch) {
        return
    }
    touchState.active = true
    touchState.x = touch.clientX
    touchState.y = touch.clientY
    touchState.startScrollTop = el?.scrollTop || 0
    touchState.locked = shouldHijackTouch(target)
    touchState.axis = ''
}

const handleWrapTouchMove = (event: TouchEvent) => {
    if (!touchState.active || !touchState.locked) {
        return
    }
    const el = playtokWrapRef.value
    const touch = event.touches[0]
    if (!el || !touch) {
        return
    }
    const deltaX = touch.clientX - touchState.x
    const deltaY = touch.clientY - touchState.y
    if (!touchState.axis) {
        if (Math.abs(deltaX) < 2 && Math.abs(deltaY) < 2) {
            return
        }
        touchState.axis = Math.abs(deltaY) >= Math.abs(deltaX) ? 'y' : 'x'
    }
    if (touchState.axis !== 'y') {
        return
    }
    event.preventDefault()
    el.scrollTop = touchState.startScrollTop - deltaY
}

const resetTouchState = () => {
    touchState.active = false
    touchState.locked = false
    touchState.axis = ''
}

const handleBack = () => {
    if (props.backFun) {
        emit('back')
        return
    }
    router.back()
}
</script>
<style lang="scss" scoped>
.header {
    @apply flex items-center fixed;
    left: 0;
    top: 0;
    z-index: 101;
    width: 100%;
    height: var(--header-height);
    padding: 0 30px;
    padding-top: constant(safe-area-inset-top);
    padding-top: env(safe-area-inset-top);
    background: #2a2d3d;

    .header-back,
    .header-right {
        min-width: 56px;
        height: 54px;
    }
    .header-back {
        width: 56px;
        height: 54px;

        svg {
            width: 100%;
            height: 100%;
        }
        &.hide {
            opacity: 0;
            pointer-events: none;
        }
    }
    .header-title {
        flex: 1;
        text-align: center;
        font-size: 32px;
        font-weight: 700;
        color: #fff;
    }
}
main {
    min-height: 100%;
}
</style>
